import routes from "@/routers/routes";
import cn from "@/utils/class-names";
import { ActionIcon, Avatar, Badge, Popover, Text, Title } from "rizzui";
// import { signOut } from "next-auth/react";
import Icons from "@/components/Icon";
import useAuthStore from "@/store/AuthStore";
import avatar from "@public/avatars/avatar-11.webp";
import { useEffect, useState } from "react";
import {
    PiSparkleDuotone,
    PiUserCircleDuotone,
    PiUserGearDuotone,
} from "react-icons/pi";
import { Link, useLocation, useNavigate } from "react-router-dom";

export default function ProfileMenu({
    buttonClassName,
    avatarClassName,
    username = false,
}: {
    buttonClassName?: string;
    avatarClassName?: string;
    username?: boolean;
}) {
    const { auth } = useAuthStore.getState();
    let picture = auth?.user.picture;
    if (!picture) {
        picture = avatar;
    } else if (!picture.startsWith("http")) {
        picture = `${import.meta.env.VITE_APP_ImagePath}${picture}`;
    }

    return (
        <ProfileMenuPopover>
            <Popover.Trigger>
                <button
                    className={cn(
                        "w-9 shrink-0 rounded-full outline-none focus-visible:ring-[1.5px] focus-visible:ring-gray-400 focus-visible:ring-offset-2 active:translate-y-px sm:w-10",
                        buttonClassName,
                    )}
                >
                    <Avatar
                        src={picture}
                        name={auth?.user.name ?? ""}
                        className={cn(
                            "!h-9 w-9 sm:!h-10 sm:!w-10",
                            avatarClassName,
                        )}
                    />
                    {!!username && (
                        <span className="username hidden text-gray-200 dark:text-gray-700 md:inline-flex">
                            {auth?.user.name}
                        </span>
                    )}
                </button>
            </Popover.Trigger>

            <Popover.Content className="z-[9999] p-0 dark:bg-gray-100 [&>svg]:dark:fill-gray-100">
                <DropdownMenu />
            </Popover.Content>
        </ProfileMenuPopover>
    );
}

function ProfileMenuPopover({ children }: React.PropsWithChildren<{}>) {
    const location = useLocation();
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        setIsOpen(false);
    }, [location.pathname]);

    return (
        <Popover
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            shadow="sm"
            placement="bottom-end"
        >
            {children}
        </Popover>
    );
}

const menuItems = [
    {
        name: "My Profile",
        href: routes.profile,
        icon: <PiUserCircleDuotone />,
    },
    {
        name: "Account Settings",
        href: routes.forms.profileSettings,
        icon: <PiUserGearDuotone />,
    },
    {
        name: "Activity Log",
        href: "#",
        icon: <PiSparkleDuotone />,
    },
];

function DropdownMenu() {
    const navigate = useNavigate();
    const { auth, logOut } = useAuthStore((s) => s);

    return (
        <div className="w-72 text-left rtl:text-right">
            <div className="flex items-center border-b border-gray-300 px-3 pb-4 pt-5">
                <Avatar
                    src={
                        auth?.user.picture
                            ? `${import.meta.env.VITE_APP_ImagePath}${auth?.user.picture}`
                            : "https://isomorphic-furyroad.s3.amazonaws.com/public/avatars/avatar-11.webp"
                    }
                    name={auth?.user?.name?.capitalize() ?? ""}
                />
                <div className="ms-3 truncate">
                    <Title as="h6" className="font-semibold truncate">
                        {auth?.user?.name}
                    </Title>
                    <Text className="text-gray-600 truncate">
                        {auth?.user.email}
                    </Text>
                    {auth?.user?.roles && (
                        <Text className="text-gray-600 truncate pt-1">
                            <Badge
                                color="info"
                                rounded="md"
                                children={auth?.user?.roles}
                            />
                        </Text>
                    )}
                </div>
            </div>
            <div className="grid px-2.5 py-2.5 font-medium text-gray-700">
                {menuItems.map((item) => (
                    <Link
                        key={item.name}
                        to={item?.href}
                        className={cn(
                            "group relative my-0.5 flex items-center justify-between rounded-md px-2.5 py-2 font-medium capitalize",
                            "text-gray-700 transition-colors duration-200 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-700/90 focus:outline-none hover:dark:bg-gray-50/50",
                        )}
                    >
                        <div className="flex items-center truncate">
                            <span
                                className={cn(
                                    "me-2 inline-flex size-5 items-center justify-center rounded-md [&>svg]:size-5",
                                    "text-gray-800 dark:text-gray-500 dark:group-hover:text-gray-700",
                                )}
                            >
                                {typeof item.icon === "string" ? (
                                    <Icons icon={item?.icon} />
                                ) : (
                                    item?.icon
                                )}
                            </span>

                            <span className="truncate">{item.name}</span>
                        </div>
                    </Link>
                ))}
            </div>
            <div className="border-t border-gray-300 px-3 pb-6 pt-5">
                <ActionIcon
                    className="h-auto w-full justify-start p-0 font-medium text-red-500 outline-none focus-within:text-red-400 hover:text-red-700 focus-visible:ring-0"
                    variant="text"
                    onClick={() => {
                        logOut();
                        navigate(routes.auth.login);
                    }}
                >
                    <Icons
                        icon="solar:logout-2-line-duotone"
                        className="me-2 inline-flex size-5 items-center justify-center rounded-md [&>svg]:size-5"
                    />
                    {/* <div className="ms-3">Sign Out</div> */}
                    Sign Out
                </ActionIcon>
            </div>
        </div>
    );
}